import { Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faCircleInfo,
  faTriangleExclamation,
  faCircleExclamation,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';

export type BannerVariant = 'info' | 'warning' | 'error';

// =============================================================================
// Banner
// A persistent, full-width status strip for a condition the user should know
// about but does not have to act on right now — a degraded upstream, a
// read-only window, a pending migration.
//
// Not a toast: a toast announces something that just happened and then leaves.
// A banner states a condition that is still true, and stays until it isn't.
// Reach for ams-toast for the former and this for the latter.
//
// Usage:
//   <ams-banner variant="warning" [message]="..." [dismissible]="true"
//               (dismissed)="hide()" />
// =============================================================================
@Component({
  selector: 'ams-banner',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
  template: `
    <div
      class="ams-banner"
      [class.ams-banner--info]="variant() === 'info'"
      [class.ams-banner--warning]="variant() === 'warning'"
      [class.ams-banner--error]="variant() === 'error'"
      role="status"
      aria-live="polite"
    >
      <fa-icon [icon]="icon()" class="ams-banner__icon" size="sm" />
      <span class="ams-banner__message" [title]="message()">{{ message() }}</span>

      @if (dismissible()) {
        <button
          class="ams-banner__dismiss"
          type="button"
          (click)="dismissed.emit()"
          aria-label="Dismiss"
          title="Dismiss"
        >
          <fa-icon [icon]="dismissIcon" size="sm" />
        </button>
      }
    </div>
  `,
  styles: `
    .ams-banner {
      display: flex;
      align-items: center;
      gap: var(--ams-space-2);
      padding: var(--ams-space-1) var(--ams-space-3);
      border: 1px solid transparent;
      border-radius: var(--ams-radius-md);
      font-size: var(--ams-font-size-sm);
      line-height: 1.4;
      /* Sits inline in a fixed-height header, so it must not force the row
         taller than the controls beside it. */
      min-width: 0;
    }

    .ams-banner__message {
      /* The header has finite width and the message is a full sentence —
         truncate rather than pushing the search bar off-screen. The title
         attribute keeps the full text reachable on hover. */
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }

    .ams-banner__icon {
      flex-shrink: 0;
    }

    .ams-banner--info {
      background: var(--ams-color-info-bg, var(--ams-color-surface-alt));
      border-color: var(--ams-color-info, var(--ams-color-border));
      color: var(--ams-color-text-primary);
    }

    .ams-banner--warning {
      background: var(--ams-color-warning-bg, var(--ams-color-surface-alt));
      border-color: var(--ams-color-warning, var(--ams-color-border));
      color: var(--ams-color-text-primary);
    }

    .ams-banner--error {
      background: var(--ams-color-error-bg, var(--ams-color-surface-alt));
      border-color: var(--ams-color-error, var(--ams-color-border));
      color: var(--ams-color-text-primary);
    }

    .ams-banner__dismiss {
      flex-shrink: 0;
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      color: inherit;
      opacity: 0.7;
      display: inline-flex;
      align-items: center;

      &:hover {
        opacity: 1;
      }
    }
  `,
})
export class BannerComponent {
  readonly variant = input<BannerVariant>('info');
  readonly message = input<string>('');
  readonly dismissible = input<boolean>(false);

  readonly dismissed = output<void>();

  readonly dismissIcon = faXmark;

  readonly icon = computed(() => {
    switch (this.variant()) {
      case 'warning':
        return faTriangleExclamation;
      case 'error':
        return faCircleExclamation;
      default:
        return faCircleInfo;
    }
  });
}
